/* eslint-disable object-curly-newline */
/* eslint-disable camelcase */
/* eslint-disable react/no-unstable-nested-components */
/* eslint-disable react/button-has-type */
/*
 * AlertBanner
 *
 * Runtime AlertBanner backed by koodo-ui-kit's <Toast mode="legacy" />.
 * Replaces the visual chrome (background, close button, icon slot) of the
 * previous custom implementation.
 *
 * The wrapper keeps every piece of business logic from Legacy:
 *   - Contentful Rich Text rendering (documentToReactComponents) with
 *     superscript and hyperlink renderers wired to analytics.
 *   - province / pages / isEnable filtering.
 *   - per-banner dismiss state (X button hides only that one).
 *   - multi-banner z-index stacking so banners stack visually consistent
 *     with the previous implementation.
 *   - analytics dispatch on link clicks and on close.
 */
import React, { useState, useContext } from 'react'
import { useLocation } from 'react-router'
import { documentToReactComponents } from '@contentful/rich-text-react-renderer'
import { BLOCKS, INLINES, MARKS } from '@contentful/rich-text-types'
import { Image, Toast } from '@telus/koodo-ui-kit'

import { doesPageMatch } from '@apps/utils/common'
import { APPLICATION_STATE, ApplicationContext } from '@apps/contexts/ApplicationContext'
import { setPageAnalytics } from '@apps/utils/Analytics'
import { ANALYTICS_EVENT_TYPES } from '@apps/utils/constants'
import useTagManagerInfo from '../../../_engine/hooks/useTagManagerInfo'

const normalize = (val) => (val ? val.toString().trim().toLowerCase() : '')

const getTextFromReactNode = (node) => {
  if (typeof node === 'string' || typeof node === 'number') {
    return node.toString()
  }
  if (Array.isArray(node)) {
    return node.map(getTextFromReactNode).join('')
  }
  if (React.isValidElement(node)) {
    return getTextFromReactNode(node.props.children)
  }
  return ''
}

const AlertBannerItem = ({
  item,
  index,
  totalAlerts,
  lang,
  currentProvince,
  currentPage,
  pushClickAnalytics
}) => {
  const [isVisible, setIsVisible] = useState(true)

  const provinceList = (item?.province || []).map(normalize)
  const pageList = (item?.pages || []).map(normalize)
  const province = normalize(currentProvince)
  const page = normalize(currentPage)

  const isAllowedProvince =
    provinceList.includes('all') || provinceList.includes(province)
  const isAllowedPage =
    pageList.includes('all') ||
    pageList.includes(page) ||
    doesPageMatch(page, pageList)

  if (!item || !item.isEnable || !isAllowedProvince || !isAllowedPage || !isVisible) {
    return null
  }

  const content = item.message?.[lang]

  const richTextOptions = {
    renderMark: { [MARKS.SUPERSCRIPT]: (text) => <sup>{text}</sup> },
    renderNode: {
      [INLINES.HYPERLINK]: (node, children) => {
        const { uri } = node.data
        const linkText = getTextFromReactNode(children).trim()
        const clickText = linkText || 'Alert banner click'
        const eventType = linkText ? `${linkText} link clicked` : 'Alert banner clicked'

        return (
          <a
            href={uri}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => {
              pushClickAnalytics({
                click_text: clickText,
                target_link: uri,
                event_type: eventType
              })
            }}
            className="underline font-semibold"
            style={{
              color: '#000000',
              overflowWrap: 'anywhere',
              wordBreak: 'break-all',
              lineBreak: 'anywhere'
            }}
          >
            {children}
          </a>
        )
      },
      [BLOCKS.PARAGRAPH]: (node, children) => (
        <span style={{ overflowWrap: 'anywhere', wordBreak: 'break-word' }}>
          {children}
        </span>
      )
    }
  }

  const handleClose = () => {
    pushClickAnalytics({
      click_text: 'Alert banner close',
      event_type: `Alert banner [${item.id || index}] close icon clicked`
    })
    setIsVisible(false)
  }

  let iconUrl = null
  if (item?.icon?.url) {
    iconUrl = item.icon.url.startsWith('http') ? item.icon.url : `https:${item.icon.url}`
  }

  const iconNode = iconUrl ? (
    <Image
      src={iconUrl}
      alt={item.icon?.text?.[lang] || 'alert icon'}
      width={24}
      height={24}
      className="shrink-0"
    />
  ) : null

  return (
    // The outer wrapper preserves the Legacy multi-banner stacking
    // (top banners over lower ones) and the Figma drop shadow. Toast
    // renders the pink chrome + close button inside.
    <div
      className="alert-banner relative print:hidden"
      style={{
        // Figma token Component/Box (background)/Feature Brand
        // (KDS Notification Banner spec, node 11594:26313).
        backgroundColor: '#E0D8FC',
        zIndex: totalAlerts - index + 50,
        boxShadow: '0px 4px 4px rgba(0, 0, 0, 0.25)'
      }}
    >
      {/*
        Centered content row at 1155px max-width — mirrors the page
        chrome. The outer div carries the full-bleed background; this
        inner wrapper aligns the message + close icon with the rest of
        the page. The Toast inside relies on its SCSS default padding
        (12/24) plus the dismissible's auto padding-right (48px) to
        reserve room for the X icon on every viewport, including
        mobile where multi-line text would otherwise overlap.
      */}
      <div style={{ maxWidth: '1155px', margin: '0 auto', width: '100%' }}>
        <Toast
          mode="legacy"
          text={documentToReactComponents(content, richTextOptions)}
          backgroundColor="transparent"
          textColor="#000000"
          dismissible
          onDismiss={handleClose}
          closeLabel="Close alert"
          // Figma token KDS Colours/Flirt for the close icon.
          closeIconColor="#7C1366"
          iconNode={iconNode}
          testId={`alert-banner-${item.id || index}`}
          tokens={{
            // Asymmetric padding: 24px on the left (Figma px-[24px])
            // and 48px on the right to keep the close button (right:
            // 12px, width: 14px) off multi-line text. Math:
            //   X left edge = root - 12 - 14 = root - 26
            //   content right edge = root - 48
            //   gap = 22px on every viewport, including mobile.
            paddingLeft: '24px',
            paddingRight: '48px',
            paddingY: '16px',
            // Figma inner row gap-[8px] (icon ↔ message).
            iconGap: '8px',
            closeButtonInsetX: '12px',
            closeButtonInsetY: '16px',
            fontFamily: 'Stag Sans, Arial, sans-serif',
            fontSize: '16px',
            fontWeight: 400,
            lineHeight: '24px',
            borderRadius: 0
          }}
        />
      </div>
    </div>
  )
}

const AlertBanner = ({ alert, lang = 'en', currentProvince = '', currentPage = '' }) => {
  const location = useLocation()
  const { [APPLICATION_STATE.CURRENT_REGION]: region } = useContext(ApplicationContext)
  const tagManagerInfo = useTagManagerInfo()

  const alerts = (Array.isArray(alert) ? alert : [alert]).filter(Boolean)

  const pushClickAnalytics = ({
    click_text,
    event_type,
    target_link,
    event_click = ANALYTICS_EVENT_TYPES.CLICK
  } = {}) => {
    window.dataLayer = window.dataLayer || []
    window.dataLayer.push(
      setPageAnalytics(
        location,
        'click',
        region,
        lang,
        { ...tagManagerInfo, click_text },
        event_type,
        event_click,
        target_link
      )
    )
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column' }}>
      {alerts.map((item, index) => (
        <AlertBannerItem
          key={item.id || index}
          item={item}
          index={index}
          totalAlerts={alerts.length}
          lang={lang}
          currentProvince={currentProvince}
          currentPage={currentPage}
          pushClickAnalytics={pushClickAnalytics}
        />
      ))}
    </div>
  )
}

export default AlertBanner
